/**
 * Plan görev komutu oluşturucu — bağlam enjeksiyonu.
 *
 * Görev şablonundaki {degisken} yer tutucuları plan değişkenleriyle doldurulur ve
 * başına plan bağlamı (plan adı, adım no, değişkenler, önceki adım özetleri) eklenir.
 * Böylece "kasa oluştur" görevi şube adını kullanıcıya tekrar sormaz — AI yalnızca
 * bağlamda OLMAYAN bilgileri (kasa adı gibi) chat'te sorabilir.
 */
import { AiPlanDetail, AiPlanTaskDto } from '../../../services/aiPlanService';

/** {degisken} yer tutucularını contextVars ile doldurur; bilinmeyen anahtar aynen kalır. */
export const fillTemplate = (template: string, vars: Record<string, string>): string =>
  template.replace(/\{([^{}]+)\}/g, (match, key: string) => {
    const value = vars[key.trim()];
    return value !== undefined && value !== '' ? value : match;
  });

/**
 * "Yarı otomatik" görev işareti: komut şablonu bu marker ile başlıyorsa işi AI değil
 * KULLANICI tamamlar (örn. şifreli personel oluşturma — şifre chat'ten geçmez).
 * Böyle görevler zincir bitince 'done' yerine 'needsReview' olur; kullanıcı işi
 * yapınca panelden "Tamamlandı işaretle" der. Marker AI'ya giden komuttan çıkarılır.
 */
export const MANUAL_COMPLETION_MARKER = '[ELLE-TAMAMLANIR]';

export const isManualCompletionTask = (task: AiPlanTaskDto): boolean =>
  task.promptTemplate.trimStart().startsWith(MANUAL_COMPLETION_MARKER);

/** Marker'ı komut metninden temizler (AI'ya iç işaretleyici sızmasın). */
const stripManualMarker = (template: string): string =>
  template.replace(MANUAL_COMPLETION_MARKER, '').trim();

/** Backend mesaj sınırı 2000 karakter (AiChatController) — komut bunu aşmamalı. */
const MAX_PROMPT_LENGTH = 2000;

/** Tek görev özetinin komutta kaplayabileceği azami uzunluk. */
const MAX_SUMMARY_LENGTH = 180;

const composePrompt = (
  task: AiPlanTaskDto,
  plan: AiPlanDetail,
  orderedTasks: AiPlanTaskDto[],
  includeSummaries: boolean
): string => {
  const stepIndex = orderedTasks.findIndex((t) => t.id === task.id);
  const stepNo = stepIndex >= 0 ? stepIndex + 1 : task.sortOrder + 1;

  const lines: string[] = [
    `Bu görev "${plan.title}" planının ${stepNo}. adımıdır (${orderedTasks.length} adımdan).`,
  ];

  const varEntries = Object.entries(plan.contextVars).filter(([, v]) => v !== '');
  if (varEntries.length > 0) {
    lines.push(`Plan bağlamı: ${varEntries.map(([k, v]) => `${k} = ${v}`).join(', ')}.`);
  }

  if (includeSummaries) {
    // Tamamlanan önceki adımların özetleri — sonraki adımlara bağlam olur
    // (örn. "şube oluşturuldu, id=42" bilgisini kasa görevine taşır)
    const doneSummaries = orderedTasks
      .filter((t) => t.id !== task.id && t.status === 'done' && t.resultSummary)
      .map((t) => `- ${t.title}: ${(t.resultSummary as string).slice(0, MAX_SUMMARY_LENGTH)}`);
    if (doneSummaries.length > 0) {
      lines.push(`Önceki adımlarda yapılanlar:\n${doneSummaries.join('\n')}`);
    }
  }

  lines.push(
    'Bağlamda verilen bilgileri kullanıcıya TEKRAR SORMA; yalnızca bağlamda olmayan eksik bilgileri sor.'
  );
  lines.push(`GÖREV: ${fillTemplate(stripManualMarker(task.promptTemplate), plan.contextVars)}`);

  // Not: aşağıdaki talimatlar GÖREV'den SONRA — komut 2000 karakter sınırından kırpılırsa
  // önce bu talimatlar feda edilir, asıl görev metni korunur.
  // Tekrarlanabilirlik: kullanıcı chat'i durdurup işi elle yapmış olabilir — oluşturma
  // eylemlerinden önce varlık kontrolü şart, yoksa aynı kayıt ikinci kez oluşur.
  lines.push(
    'Yeni kayıt oluşturmadan ÖNCE ilgili listeleme eylemiyle (listBranches, listCashboxes, ' +
      'listBankAccounts, listQuickSaleScreens vb.) aynı adlı kaydın zaten var olup olmadığını KONTROL ET — ' +
      'kullanıcı işi daha önce elle yapmış olabilir. Kayıt zaten varsa yeniden oluşturma; mevcut kaydı kullan.'
  );
  // Kilit çözümü: iş zaten yapılmışsa AI hiç eylem çağırmadan metinle bitirir ve görev
  // panelde 'çalışıyor' asılı kalırdı — completeAiPlanTask ile kendisi kapatabilsin.
  lines.push(
    `Bu görevin taskId'si ${task.id}. İstenen iş ZATEN yapılmış durumdaysa (ör. kayıt önceden oluşturulmuş) ` +
      `veya görevi yeni bir eylem çağırmadan bitirdiysen completeAiPlanTask eylemini (taskId=${task.id}) çağırarak ` +
      `görevi tamamlandı olarak işaretle; işaretlemeden bitirme.`
  );

  return lines.join('\n\n');
};

/** Sınırı aşan komutta önce özetler atılır, yine aşıyorsa sondan kırpılır. */
const capLength = (prompt: string, fallback: () => string): string => {
  if (prompt.length <= MAX_PROMPT_LENGTH) return prompt;
  const withoutSummaries = fallback();
  return withoutSummaries.length <= MAX_PROMPT_LENGTH
    ? withoutSummaries
    : withoutSummaries.slice(0, MAX_PROMPT_LENGTH);
};

/**
 * Görev için AI'ya gönderilecek tam komutu üretir:
 * plan bağlamı başlığı + doldurulmuş görev komutu.
 */
export const buildTaskPrompt = (task: AiPlanTaskDto, plan: AiPlanDetail): string => {
  const orderedTasks = [...plan.tasks].sort((a, b) => a.sortOrder - b.sortOrder || a.id - b.id);
  return capLength(
    composePrompt(task, plan, orderedTasks, true),
    () => composePrompt(task, plan, orderedTasks, false)
  );
};

/**
 * Metin otomatik üretilmiş bir plan görev komutu mu? Görev komutları chat'te
 * gösterilmez (canlı gönderimde hidden, geçmiş yüklemede bu filtreyle).
 */
export const isPlanTaskPrompt = (text: string): boolean => {
  const s = (text || '').trimStart();
  return /^Bu görev ".+" planının \d+\. adımıdır/.test(s) || s.startsWith('Aşağıdaki görev yarım kaldı');
};

/** Yarım kalan/başarısız görevi sürdürme komutu ("Devam et" butonu). */
export const buildContinueTaskPrompt = (task: AiPlanTaskDto, plan: AiPlanDetail): string => {
  const orderedTasks = [...plan.tasks].sort((a, b) => a.sortOrder - b.sortOrder || a.id - b.id);
  const prefix =
    'Aşağıdaki görev yarım kaldı — kaldığın yerden tamamla. Daha önce yapılmış adımları TEKRARLAMA, eksik kalanları yap.\n\n';
  const prompt = prefix + composePrompt(task, plan, orderedTasks, true);
  if (prompt.length <= MAX_PROMPT_LENGTH) return prompt;
  const fallback = prefix + composePrompt(task, plan, orderedTasks, false);
  return fallback.length <= MAX_PROMPT_LENGTH ? fallback : fallback.slice(0, MAX_PROMPT_LENGTH);
};
